// Cookie consent state — single source of truth for whether the user has
// made a cookie choice and what it was.
//
// Three states (cookie value):
//   'accepted'  — user clicked Accept All, run analytics + marketing
//   'essential' — user clicked Reject (essential-only, no analytics)
//   missing     — no choice yet, banner shows
//
// Why a cookie + not localStorage: the consent decision needs to be
// readable by the same backend / Netlify edge / Wix that runs cookies
// for cart + auth, AND it needs to survive incognito → not-incognito
// promotion the same way the cart cookie does.

'use client';

export type ConsentChoice = 'accepted' | 'essential';

const COOKIE_NAME = 'np_consent';
const COOKIE_MAX_AGE_DAYS = 365;

export const CONSENT_CHANGED = 'np:consent-changed';

export function getConsent(): ConsentChoice | null {
  if (typeof document === 'undefined') return null;
  const target = `${COOKIE_NAME}=`;
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(target)) {
      const v = decodeURIComponent(part.slice(target.length));
      if (v === 'accepted' || v === 'essential') return v;
    }
  }
  return null;
}

export function setConsent(choice: ConsentChoice): void {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + COOKIE_MAX_AGE_DAYS * 86_400_000).toUTCString();
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${COOKIE_NAME}=${choice}; expires=${expires}; path=/; SameSite=Lax${secure}`;
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED, { detail: choice }));
}

export function clearConsent(): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED, { detail: null }));
}
